import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import PlannedChangesReview, { ACTION_LABELS } from './PlannedChangesReview';

const CHECK_LABELS = {
  payload_checksum: 'Package checksum',
  package_signature: 'Package signature',
  source_environment: 'Source environment allowed',
  package_schema_version: 'Package format version',
  application_version: 'Application version parity',
  migration_state: 'Database migration parity',
  sponsor: 'Sponsor exists in this environment',
  entity_counts: 'Package contents match its manifest',
  devices: 'Referenced devices exist',
  screen_templates: 'Referenced screen templates exist',
  vpg_library_questions: 'Referenced VPG library questions exist',
  collision: 'Study collision check',
};

const CLASSIFICATION_BADGES = {
  new_study: { label: 'New study', classes: 'badge-success' },
  re_promotion: { label: 'Re-promotion', classes: 'badge-info' },
  conflict: { label: 'Conflict', classes: 'badge-danger' },
};

const PreflightReportView = ({ manifest, target, report, payload }) => {
  if (!report) return null;
  const badge = CLASSIFICATION_BADGES[report.classification] || null;

  return (
    <div className="space-y-6">
      {/* Candidate summary */}
      <div className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
              Candidate {manifest?.candidate_label}
            </h3>
            <p className="mt-0.5 break-all font-mono text-xs text-gray-500 dark:text-gray-400">
              {manifest?.candidate_id}
            </p>
          </div>
          {badge && (
            <span className={`badge badge-dot ${badge.classes}`}>
              {badge.label}
            </span>
          )}
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 border-t border-gray-100 pt-4 text-[13px] sm:grid-cols-2 dark:border-white/[0.06]">
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">Study</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-50">
              {manifest?.study?.title} ({manifest?.study?.study_code})
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">Sponsor</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-50">{manifest?.sponsor?.sponsor_name}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">From</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-50">
              {manifest?.source_environment} @ {manifest?.application_version}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">Exported by</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-50">
              {manifest?.exported_by || 'unattributed'}
              {manifest?.exported_at ? `, ${new Date(manifest.exported_at).toLocaleString()}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">Into</dt>
            <dd className="mt-0.5 font-semibold text-gray-900 dark:text-gray-50">
              {target?.environment} @ {target?.application_version}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">This environment accepts</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-50">
              {target?.allowed_source_environments?.length
                ? `builds from ${target.allowed_source_environments.join(', ')}`
                : 'builds from any environment'}
              {target?.signing_enforced ? ', signed only' : ''}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Checksum</dt>
            <dd className="mt-0.5 break-all font-mono text-xs text-gray-700 dark:text-gray-300">
              {manifest?.payload_checksum}
            </dd>
          </div>
        </dl>
        {manifest?.entity_counts && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {Object.entries(manifest.entity_counts).map(([entity, count]) => (
              <span
                key={entity}
                className="badge badge-gray"
              >
                {entity.replace(/_/g, ' ')}: <span className="tabular-nums">{count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Checks */}
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4">
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
            Preflight checks
          </h3>
          {report.checks?.length > 0 && (
            <span className="badge badge-gray tabular-nums">
              {report.checks.filter((check) => check.passed).length}/{report.checks.length}
            </span>
          )}
        </div>
        <ul className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
          {report.checks?.map((check) => (
            <li key={check.name} className="flex items-start gap-3 px-5 py-3">
              {check.passed ? (
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success-600 dark:text-success-400" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600 dark:text-danger-400" />
              )}
              <div className="min-w-0">
                <p className={`text-[13.5px] font-medium ${check.passed ? 'text-gray-900 dark:text-gray-50' : 'text-danger-700 dark:text-danger-400'}`}>
                  {CHECK_LABELS[check.name] || check.name}
                </p>
                {check.detail && (
                  <p className="text-[13px] text-gray-500 dark:text-gray-400">{check.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Planned changes — deep review when the parsed package is available,
          otherwise the compact per-entity action table */}
      {payload ? (
        <PlannedChangesReview
          payload={payload}
          plannedActions={report.planned_actions}
          classification={report.classification}
        />
      ) : report.planned_actions && Object.keys(report.planned_actions).length > 0 && (
        <div className="card overflow-hidden">
          <h3 className="px-5 py-4 text-[15px] font-semibold text-gray-900 dark:text-gray-50">
            Planned actions
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-y border-gray-100 dark:border-white/[0.06]">
                  <th className="th-cell">Content</th>
                  <th className="th-cell">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                {Object.entries(report.planned_actions).map(([entity, action]) => (
                  <tr key={entity}>
                    <td className="td-cell capitalize text-gray-900 dark:text-gray-50">
                      {entity.replace(/_/g, ' ')}
                    </td>
                    <td className="td-cell">
                      {ACTION_LABELS[action] || action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreflightReportView;
